import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { errorMessage, isApiError } from '@/api/client';
import { useAuth } from '@/auth/context';
import { Button, ErrorNote, TextField } from '@/components/ui';
import { MOCK } from '@/config';
import { AuthLayout, linkClass } from './AuthLayout';

export function SignIn() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      const next = params.get('next');
      navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/', { replace: true });
    } catch (err) {
      setError(isApiError(err, 401) ? 'That email and password don’t match.' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="See every number that matters in one place."
      footer={
        <>
          New to MyDash?{' '}
          <Link to="/sign-up" className={linkClass}>
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {error && <ErrorNote title="Couldn’t sign in" message={error} />}
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className="-mt-1 text-right">
          <Link to="/forgot-password" className="text-sm text-muted-ink underline-offset-4 hover:text-ink hover:underline">
            Forgot your password?
          </Link>
        </div>
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          Sign in
        </Button>
        {MOCK && (
          <p className="rounded-control border border-dashed border-border p-3 text-xs text-muted-ink">
            Mock mode: sign in with <span className="font-mono text-ink">owner@example.com</span> /{' '}
            <span className="font-mono text-ink">mydash-demo</span>, or create a new account.
          </p>
        )}
      </form>
    </AuthLayout>
  );
}
